import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { defaultSerialize, defaultDeserialize } from './queryParamUtils';

/**
 * Custom hook for managing state synchronized with URL query parameters
 * 
 * @param {string} paramName - The query parameter name
 * @param {any} defaultValue - The default value if parameter doesn't exist
 * @param {Object} options - Configuration options
 * @param {boolean} options.replace - Whether to use replaceState instead of pushState (default: true)
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 0)
 * @param {Function} options.serialize - Custom serialization function
 * @param {Function} options.deserialize - Custom deserialization function
 * @returns {[any, Function]} - [currentValue, setValue] similar to useState
 */
const useQueryParamState = (paramName, defaultValue, options = {}) => {
  const {
    replace = true,
    debounceMs = 0,
    serialize = defaultSerialize,
    deserialize = defaultDeserialize
  } = options;

  const location = useLocation();
  const navigate = useNavigate();
  
  // Refs to track state without triggering re-renders
  const isInitialMount = useRef(true);
  const skipNextURLUpdate = useRef(false);
  const previousLocation = useRef(location);
  const timeoutRef = useRef(null);
  
  // Parse from URL or use default
  const parseFromURL = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const urlValue = params.get(paramName);
    if (urlValue === null) {
      return defaultValue;
    }
    try {
      return deserialize(urlValue);
    } catch (error) {
      console.warn(`Failed to deserialize query param "${paramName}":`, error);
      return defaultValue;
    }
  }, [paramName, defaultValue, location.search, deserialize]);

  // State
  const [value, setInternalValue] = useState(parseFromURL);
  
  // Compare values properly (handles objects and arrays)
  const areEqual = useCallback((a, b) => {
    if (a === b) return true;
    
    // Special handling for objects and arrays
    if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
      try {
        return JSON.stringify(a) === JSON.stringify(b);
      } catch (e) {
        return false;
      }
    }
    
    return false;
  }, []);

  // Update URL based on current value
  const updateURL = useCallback((newValue) => {
    // Don't update URL during initial mount or when flagged to skip
    if (isInitialMount.current || skipNextURLUpdate.current) {
      skipNextURLUpdate.current = false;
      return;
    }
    
    // Clone current search params
    const newSearchParams = new URLSearchParams(location.search);
    
    // Update or remove parameter
    if (newValue === defaultValue || newValue === '' || newValue === null || newValue === undefined) {
      newSearchParams.delete(paramName);
    } else {
      try {
        newSearchParams.set(paramName, serialize(newValue));
      } catch (error) {
        console.warn(`Failed to serialize value for query param "${paramName}":`, error);
        return;
      }
    }
    
    // Construct new URL
    const newSearch = newSearchParams.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    const currentPath = `${location.pathname}${location.search}`;
    
    // Only navigate if the URL actually changed
    if (newPath !== currentPath) {
      // Clear any pending timeouts
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
      
      // Debounce the navigation
      timeoutRef.current = setTimeout(() => {
        navigate(newPath, { replace });
        timeoutRef.current = null;
      }, debounceMs);
    }
  }, [paramName, defaultValue, serialize, location, navigate, replace, debounceMs]);

  // Sync state to URL
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Only update URL if location changed and it wasn't us who changed it
    if (previousLocation.current.search !== location.search) {
      const newValueFromURL = parseFromURL();
      const currentValue = value;
      
      // Only update state if value actually changed
      if (!areEqual(newValueFromURL, currentValue)) {
        // Flag to skip the URL update that would normally be triggered by this state change
        skipNextURLUpdate.current = true;
        setInternalValue(newValueFromURL);
      }
    }
    
    // Update previous location reference
    previousLocation.current = location;
  }, [location, parseFromURL, areEqual, value]);

  // Custom setter that updates both state and URL
  const setValue = useCallback((newValueOrFunction) => {
    setInternalValue(prevValue => {
      // Handle function updaters
      const resolvedValue = typeof newValueOrFunction === 'function'
        ? newValueOrFunction(prevValue)
        : newValueOrFunction;
        
      // Schedule URL update on next tick to avoid React batching issues
      setTimeout(() => {
        updateURL(resolvedValue);
      }, 0);
        
      return resolvedValue;
    });
  }, [updateURL]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [value, setValue];
};

export default useQueryParamState;
export { defaultSerialize, defaultDeserialize };